import React, { useState, useEffect } from 'react';
import "./style.css";
import Draggable from 'react-draggable';
import Time from "./Time";
import moment from 'moment'; // Global States for faster scheduling

import { useStore } from "../../contexts/store";

const TimeSlot = ({
  clientSideRef,
  expertSideRef
}) => {
  const {
    state,
    dispatch
  } = useStore();
  const {
    firstMatch,
    duration,
    schedule,
    availabilityStore_imp,
    clientZone,
    expertZone,
    dayToRender,
    opsTime
  } = state;
  const [isDragging, setDragging] = useState(false);
  const [opsTimeString, setOpsTimeString] = useState('');
  useEffect(() => {
    // Set default value for opsTime
    if (opsTime === null && opsTimeString) {
      const splitString = opsTimeString.split(':').map(x => parseInt(x));
      const opsTime = {
        hour: splitString[0],
        minutes: splitString[1]
      };
      dispatch({
        type: 'UPDATE',
        payload: {
          opsTime
        }
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [opsTimeString]);
  const client = clientSideRef.current;
  const expert = expertSideRef.current;

  const getYPosition = timestamp => {
    const hourPosition = new Date(timestamp).getHours() * 23;
    const minutePosition = new Date(timestamp).getMinutes() / 15 * 5.75;
    return hourPosition + minutePosition;
  };

  const initialDelta = () => {
    let y = new Date().getHours() * 23 + 23;

    if (!!schedule.timeslots.length && schedule.timeslots[0].time.start > new Date().getTime()) {
      y = getYPosition(schedule.timeslots[0].time.start);
    }

    if (firstMatch > 0) {
      y = getYPosition(firstMatch);
    }

    return y;
  };

  const [deltaPosition, setDeltaPosition] = useState({
    y: initialDelta()
  }); // Timeslot is not rendered only once. thus, useEffect to update dayToRender is moved to FasterScheduling.js
  // Only update when render date of calendar UI changes a.k.a clientRenderDateString

  useEffect(() => {
    // This function sets the y position of the draggable timeslot based on the following conditions
    const updateDraggableTimeslot = () => {
      // Checking if dayToRender is a moment object just in case
      if (!isDragging && dayToRender && moment.isMoment(dayToRender)) {
        const clientAv = client.props.schedules.map(s => moment(s.start).format('DDMMYYYY'));
        const expertAv = expert.props.schedules.map(s => moment(s.start).format('DDMMYYYY')); // If the date rendering now match the first timeslot's start time

        const {
          timeslots
        } = schedule;

        if (!!timeslots.length && timeslots[0].time && moment(timeslots[0].time.start).isSame(dayToRender, 'day')) {
          setDeltaPosition({
            y: getYPosition(timeslots[0].time.start)
          });
          return;
        }

        const clientRenderDateString = dayToRender.format('DDMMYYYY');

        if (clientAv.includes(clientRenderDateString) && expertAv.includes(clientRenderDateString)) {
          const clietArr2 = client.props.schedules.filter(x => moment(x.start).isSame(dayToRender, 'day'));
          const expertArr2 = expert.props.schedules.filter(x => moment(x.start).isSame(dayToRender, 'day'));
          const matchingAvailabilities = []; // TODO: avoid 2 dimensional loop

          clietArr2.forEach(c => {
            expertArr2.forEach(e => {
              if (c.start >= e.start && c.start <= e.end) {
                matchingAvailabilities.push(c);
              } else if (e.start >= c.start && e.start <= c.end) {
                matchingAvailabilities.push(e);
              } else {
                return;
              }
            });
          });

          if (matchingAvailabilities.length) {
            const delta = matchingAvailabilities.sort((a, b) => Number(moment(a.start).format('x')) - Number(moment(b.start).format('x')))[0].start;
            setDeltaPosition({
              y: getYPosition(delta)
            });
            return;
          }

          const day = client.props.schedules.find(x => moment(x.start).isSame(dayToRender, 'day')).start;
          setDeltaPosition({
            y: getYPosition(day)
          });
          return;
        }

        if (clientAv.includes(clientRenderDateString)) {
          const day = client.props.schedules.find(x => moment(x.start).isSame(dayToRender, 'day')).start;
          setDeltaPosition({
            y: getYPosition(day)
          });
          return;
        }

        if (expertAv.includes(clientRenderDateString)) {
          const day = expert.props.schedules.find(x => moment(x.start).isSame(dayToRender, 'day')).start;
          setDeltaPosition({
            y: getYPosition(day)
          });
          return;
        }
      }
    };

    updateDraggableTimeslot(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayToRender]);

  const onStart = e => {
    setDragging(true);
    const clientItems = clientSideRef.current.calendarInst._controller.schedules.items;
    const clientSchedules = Object.keys(clientItems).map(event => clientItems[event]);
    const expertItems = expertSideRef.current.calendarInst._controller.schedules.items;
    const expertSchedules = Object.keys(expertItems).map(event => expertItems[event]);
    /** @type {HTMLElement[]} */
    // @ts-ignore

    const slots = document.querySelectorAll('.slot');
    const currentDay = clientSideRef.current.calendarInst._renderDate._date;

    const dayString = input => input.toString().slice(0, 15);

    const currentAv = party => {
      const result = [];
      const filterToday = party.filter(av => dayString(av.start.toDate()) === dayString(currentDay));
      const hoursToday = filterToday.map(x => [parseInt(x.start.toDate().toString().slice(16, 18)), parseInt(x.end.toDate().toString().slice(16, 18))]);
      hoursToday.forEach(timePair => {
        for (let i = timePair[0]; i < timePair[1]; i++) {
          result.push(i);
        }
      });
      return result;
    };

    const bothAv = currentAv(clientSchedules).concat(currentAv(expertSchedules)).filter((a, i, aa) => aa.indexOf(a) === i && aa.lastIndexOf(a) !== i);
    const clientAvToday = currentAv(clientSchedules);
    const expertAvToday = currentAv(expertSchedules);
    const noClientAv = clientSchedules.length === 0;
    const noExpertAv = expertSchedules.length === 0;

    const buildSlots = () => {
      if (noExpertAv && noClientAv) {
        Array.from(slots).map(slot => slot.style.backgroundColor = '#05ff412b');
      } else if (clientAvToday.length > 0 && noExpertAv) {
        Array.from(slots).map(slot => slot.style.backgroundColor = clientAvToday.includes(parseInt(slot.id)) ? '#05ff412b' : '#ff00002b');
      } else if (expertAvToday.length > 0 && noClientAv) {
        Array.from(slots).map(slot => slot.style.backgroundColor = expertAvToday.includes(parseInt(slot.id)) ? '#05ff412b' : '#ff00002b');
      } else {
        Array.from(slots).map(slot => slot.style.backgroundColor = bothAv.includes(parseInt(slot.id)) ? '#05ff412b' : '#ff00002b');
      }
    };

    buildSlots();
    Array.from(slots).map(slot => slot.classList.remove('hide'));
  };

  const getTimezone = () => {
    return moment.tz.guess();
  };

  function formatTimezone(momentDate) {
    const timezoneIdentifier = momentDate.format('z');
    const timezoneOffsetInMinutes = momentDate.utcOffset();
    const prefix = timezoneOffsetInMinutes >= 0 ? '+' : '';
    const hours = Math.floor(timezoneOffsetInMinutes / 60);
    const minutes = timezoneOffsetInMinutes % 60;
    const minutesString = minutes !== 0 ? ':' + minutes : '';
    return `${timezoneIdentifier}${prefix}${hours}${minutesString}`;
  }

  function formatDateTime(date, showTimezone = true) {
    const m = moment.tz(date, getTimezone());
    const formattedDate = m.format('MMM DD, YYYY');
    const formattedTime = m.format('HH:mm');

    if (showTimezone) {
      const formattedTimezone = formatTimezone(m);
      const format = `${formattedDate} at ${opsTimeString} ${formattedTimezone}`;
      return format;
    } else {
      return `${formattedDate}, ${formattedTime}`;
    }
  }

  const setzmTxt = str => {
    const txt = str.split('-')[0];

    if (txt !== opsTimeString) {
      // New OpsTime set
      const splitString = txt.split(':').map(x => parseInt(x));
      const newOpsTime = {
        hour: splitString[0],
        minutes: splitString[1]
      };
      setOpsTimeString(txt);
      dispatch({
        type: 'UPDATE',
        payload: {
          opsTime: newOpsTime
        }
      });
    }
  };

  const oneDay = 1000 * 60 * 60 * 24;

  const setExpertDayOffset = index => {
    let expertDayOffset = 0;

    if (index < 96) {
      expertDayOffset = oneDay * -1;
    }

    if (index > 191) {
      expertDayOffset = oneDay * 1;
    }

    dispatch({
      type: 'UPDATE',
      payload: {
        expertDayOffset
      }
    });
  };

  const setClientDayOffset = index => {
    let clientDayOffset = 0;

    if (index < 96) {
      clientDayOffset = oneDay * -1;
    } else if (index > 191) {
      clientDayOffset = oneDay * 1;
    }

    dispatch({
      type: 'UPDATE',
      payload: {
        clientDayOffset
      }
    });
  };

  const startIndex = Math.round(deltaPosition.y / 5.75 + 96);
  const tz = clientZone.timezoneOffset / 15;
  const tzx = expertZone.timezoneOffset / 15;
  const tzOps = new Date().getTimezoneOffset() / 15;

  const onStop = (e, ui) => {
    let timeStr;

    if (availabilityStore_imp.persistedScheduleInfo && availabilityStore_imp.persistedScheduleInfo.zoomMeeting && availabilityStore_imp.persistedScheduleInfo.zoomMeeting.start_time) {
      const date = moment(clientSideRef.current.calendarInst._renderDate._date).format('MMM DD, YYYY');
      const tz = formatDateTime(new Date(availabilityStore_imp.persistedScheduleInfo.zoomMeeting.start_time).getTime()).split(' ');
      const strtr = `${date} at ${opsTimeString}${tz[tz.length - 1]}`;
      timeStr = strtr;
    } else if (schedule.timeslots[0]) {
      const date = moment(clientSideRef.current.calendarInst._renderDate._date).format('MMM DD, YYYY');
      const tz = formatDateTime(schedule.timeslots[0].time.start).split(' ');
      const strtr = `${date} at ${opsTimeString}${tz[tz.length - 1]}`;
      timeStr = strtr;
    }

    dispatch({
      type: 'UPDATE',
      payload: {
        timeOnDrop: timeStr,
        onQuick: false,
        onCustom: false
      }
    });
    const slots = document.querySelectorAll('.slot');
    Array.from(slots).map(slot => slot.classList.add('hide'));
    setDragging(false);
    setClientDayOffset(startIndex + tzOps + tz);
    setExpertDayOffset(startIndex + tzOps + tzx);
  };

  const handleDrag = (e, ui) => {
    const {
      y
    } = deltaPosition;
    setDeltaPosition({
      y: y + ui.deltaY
    });
  };

  let timeSlotClass = 'timeSlotContainer';

  if (duration === 30) {
    timeSlotClass += ' halfHour';
  }

  if (duration === 90) {
    timeSlotClass += ' hourNHalf';
  }

  if (duration === 120) {
    timeSlotClass += ' twoHours';
  }

  return clientSideRef && /*#__PURE__*/React.createElement(Draggable, {
    onStart: onStart,
    onStop: onStop,
    onDrag: handleDrag,
    bounds: "parent",
    axis: "y",
    defaultPosition: {
      x: 0,
      y: deltaPosition.y
    },
    position: {
      x: 0,
      y: deltaPosition.y
    },
    grid: [23 / 4, 23 / 4],
    scale: 1
  }, /*#__PURE__*/React.createElement("div", {
    id: "timeslot",
    className: timeSlotClass
  }, /*#__PURE__*/React.createElement(Time, {
    deltaPosition: deltaPosition,
    setzmTxt: setzmTxt,
    setExpertDayOffset: setExpertDayOffset,
    setClientDayOffset: setClientDayOffset
  })));
};

export default TimeSlot;